import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useTenant } from '../../../hooks/useTenant';
import NeoLoader from '../../../components/Common/NeoLoader';
import { Breadcrumb, NeoButton, NeoCard, NeoDatePicker, NeoInput } from '../../../components/Common';

import { useCashier } from './hooks/useCashier';
import { CashierTable } from './components/CashierTable';
import OpenCashierModal from './components/OpenCashierModal';
import CloseCashierModal from './components/CloseCashierModal';
import PrintableCashierReport from './components/PrintableCashierReport';

const CashierPage = () => {
    document.title = "Caixa | NeoApp - Sistema de Gestão";

    const { tenantName, tenantLogo, tenantSettings, user } = useTenant();
    const printRef = useRef();

    const {
        selectedDate,
        setSelectedDate,
        summary,
        records,
        loading,
        currentShift,
        availableShifts,
        canViewAllCashier,
        modals,
        toggleOpenModal,
        toggleCloseModal,
        isProcessingShift,
        handleSelectShift,
        handleOpenShift,
        handleCloseShift
    } = useCashier();

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        content: () => printRef.current,
        documentTitle: `Fechamento_Caixa_${selectedDate}`,
    });

    return (
        <React.Fragment>
            <div className="container-fluid p-0">
                {/* TOP BAR: Cabeçalho & Filtro de Período Lado a Lado (Super Limpo e Sem Card) */}
                <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between mb-4 gap-3">
                    <div>
                        <Breadcrumb title="Caixa" />
                        <p className="text-muted mb-0 font-size-13 mt-0.5">
                            Gestão de turnos, controle de saldo, lançamentos e fluxo de caixa diário.
                        </p>
                    </div>

                    <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2 flex-shrink-0">
                        {canViewAllCashier && availableShifts.length > 0 && (
                            <div className="neo-filter-select-sm">
                                <NeoInput
                                    type="select"
                                    value={currentShift?.id || ""}
                                    onChange={(e) => handleSelectShift(e.target.value)}
                                    size="sm"
                                    className="mb-0"
                                >
                                    {availableShifts.map((shift) => (
                                        <option key={shift.id} value={shift.id}>
                                            {(shift.operatorName || shift.operatorId || "Operador").toUpperCase()} ({shift.status === "OPEN" ? "ABERTO" : "FECHADO"})
                                        </option>
                                    ))}
                                </NeoInput>
                            </div>
                        )}
                        <NeoDatePicker
                            value={selectedDate}
                            onChange={(dateStr) => setSelectedDate(dateStr)}
                            maxDate={new Date().toISOString().split("T")[0]}
                            size="sm"
                            className="mb-0 neo-datepicker-filter"
                        />
                        <NeoButton
                            variant="outline-secondary"
                            onClick={handlePrint}
                            title="Imprimir Fechamento de Caixa"
                            icon="mdi-printer"
                            size="sm"
                            square
                            className="neo-filter-search-btn"
                        />
                        <NeoButton
                            variant={currentShift ? "danger" : "primary"}
                            size="sm"
                            onClick={currentShift ? toggleCloseModal : toggleOpenModal}
                            icon={currentShift ? "mdi-lock" : "mdi-lock-open"}
                            className="neo-filter-search-btn px-3"
                        >
                            {currentShift ? "Fechar Caixa" : "Abrir Caixa"}
                        </NeoButton>
                    </div>
                </div>

                <div className="border-top mb-4"></div>

                {/* ═══ PRINTABLE REPORT (HIDDEN) ═══ */}
                <div style={{ display: 'none' }}>
                    <div ref={printRef}>
                        <PrintableCashierReport
                            currentShift={currentShift}
                            summary={summary}
                            records={records}
                            user={user}
                            tenantName={tenantName || tenantSettings?.name}
                            tenantLogo={tenantLogo || tenantSettings?.logo}
                        />
                    </div>
                </div>

                {/* ═══ CAIXA ACCORDION & TABLE ═══ */}
                {loading && !currentShift ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
                        <NeoLoader type="inline" loading={loading} />
                    </div>
                ) : !currentShift ? (
                    <NeoCard
                        title="Controle de Caixa"
                        headerIcon="mdi-cash-register"
                        className="mt-2"
                        bodyClassName="p-5 text-center"
                    >
                        <div className="neo-empty-state-icon mx-auto mb-4">
                            <i className="mdi mdi-cash-register font-size-36 text-accent" />
                        </div>
                        <h4 className="mt-3 text-dark fw-bold font-size-18 neo-font-outfit">Nenhum caixa encontrado para este escopo</h4>
                        <p className="text-muted mb-4 font-size-13 mx-auto neo-empty-state-desc">
                            {canViewAllCashier
                                ? "Nenhum caixa foi encontrado para a data atual. Selecione um período diferente ou realize a abertura de um novo caixa para iniciar."
                                : "Abra o seu caixa para começar a registrar ou visualizar as movimentações do seu turno."}
                        </p>
                        <div>
                            <NeoButton
                                variant="primary"
                                onClick={toggleOpenModal}
                                size="md"
                                icon="mdi-lock-open"
                            >
                                Abrir Caixa Agora
                            </NeoButton>
                        </div>
                    </NeoCard>
                ) : (
                    <CashierTable
                        records={records}
                        loading={loading}
                        summary={summary}
                        selectedDate={selectedDate}
                        currentShift={currentShift}
                        onCloseCashier={toggleCloseModal}
                    />
                )}

                {/* MODALS */}
                <OpenCashierModal
                    isOpen={modals.open}
                    toggle={toggleOpenModal}
                    onConfirm={handleOpenShift}
                    isProcessing={isProcessingShift}
                />

                <CloseCashierModal
                    isOpen={modals.close}
                    toggle={toggleCloseModal}
                    onConfirm={handleCloseShift}
                    isProcessing={isProcessingShift}
                />
            </div>
        </React.Fragment>
    );
};

export default CashierPage;
